import React, { useEffect, useState } from 'react';
import { getToken } from '../api.js';

// Small helpers shared by the member and admin chat screens.

const URL_RE = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]'"»])/gi;
// Plain text with clickable links (rel="noopener nofollow").
export function Linkified({ text }) {
  const parts = String(text || '').split(URL_RE);
  return parts.map((p, i) => (i % 2
    ? <a key={i} href={p.startsWith('www.') ? `https://${p}` : p} target="_blank" rel="noopener nofollow noreferrer">{p}</a>
    : <React.Fragment key={i}>{p}</React.Fragment>));
}

const loc = (lang) => (lang === 'en' ? 'en-CA' : 'fr-CA');
export const timeOf = (iso, lang) => new Date(iso).toLocaleTimeString(loc(lang), { hour: '2-digit', minute: '2-digit' });
export function dayLabel(iso, lang, t) {
  const d = new Date(iso); const today = new Date();
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return t('chat.today');
  if (d.toDateString() === y.toDateString()) return t('chat.yesterday');
  return d.toLocaleDateString(loc(lang), { weekday: 'long', day: 'numeric', month: 'long', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
}
// List time: hour today, weekday this week, else date.
export function shortWhen(iso, lang) {
  if (!iso) return '';
  const d = new Date(iso); const now = new Date();
  if (d.toDateString() === now.toDateString()) return timeOf(iso, lang);
  if (now - d < 6 * 86400000) return d.toLocaleDateString(loc(lang), { weekday: 'short' });
  return d.toLocaleDateString(loc(lang), { day: 'numeric', month: 'short' });
}
export const fileSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
export function fileIcon(mime, name = '') {
  if (mime === 'application/pdf') return 'PDF';
  if (/sheet|excel/.test(mime) || /\.xlsx?$/i.test(name)) return 'XLS';
  if (/word/.test(mime) || /\.docx?$/i.test(name)) return 'DOC';
  return 'FILE';
}

// Private file (needs the login token): fetched once, shown from a blob URL.
const blobCache = new Map();
export function useAuthBlob(url) {
  const [src, setSrc] = useState(blobCache.get(url) || null);
  useEffect(() => {
    if (!url || blobCache.has(url)) { setSrc(blobCache.get(url) || null); return undefined; }
    let alive = true;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => (r.ok ? r.blob() : null))
      .then((b) => { if (b && alive) { const u = URL.createObjectURL(b); blobCache.set(url, u); setSrc(u); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [url]);
  return src;
}
export async function downloadPrivate(url, name) {
  const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error('download_failed');
  const u = URL.createObjectURL(await r.blob());
  const a = document.createElement('a'); a.href = u; a.download = name || 'file'; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 30000);
}

export const EMOJIS = ['😀', '😊', '🙂', '😉', '😂', '🙏', '👍', '👌', '👏', '🤝', '💪', '🎉', '✅', '❌', '⚠️', '❓', '💬', '📦', '🚚', '🚢', '✈️', '📄', '📎', '💰', '💵', '📈', '📉', '⏰', '📅', '📞', '✉️', '🌍', '🇨🇦', '🔥', '⭐', '❤️', '🤔', '😅', '🙌', '👀'];
